import { AlignerScheduleSection } from '../AlignerScheduleSection';
import {
    DEFAULT_ALIGNER_FIELDS,
    anyAlignerFieldEnabled
} from '@odo/shared-ui/clinical/alignerFields';
import { SectionToggle } from '../SectionToggle';
import { baseFieldClasses } from '@odo/shared-ui/components/fieldClasses';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import type { SectionToggles, AlignerFieldToggles } from '@odo/shared-ui/clinical/types';

export interface ClinicalFormFields {
    date: string;
    patientName: string;
    numberOfAligners: string;
    alignerWear: string;
    dmScanFrequency: string;
    alignerChangeFrequency: string;
}

interface ClinicalFormStepProps {
    patientId: string;
    fields: ClinicalFormFields;
    onFieldChange: (field: keyof ClinicalFormFields, value: string) => void;
    nextAppointment: string;
    onNextAppointmentChange: (value: string) => void;
    nextAppointmentLabel: string;
    nextAppointmentPlaceholder?: string;
    alignerWearOptions: readonly string[];
    orthopulseStatus?: string | null;
    toggles: SectionToggles;
    onToggleChange: (toggles: SectionToggles) => void;
}

interface FieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    type?: 'text' | 'date';
    placeholder?: string;
}

function Field({
    label,
    value,
    onChange,
    readOnly = false,
    type = 'text',
    placeholder
}: FieldProps) {
    const readOnlyClasses = readOnly ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '';
    return (
        <div className="flex flex-col">
            <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                readOnly={readOnly}
                placeholder={placeholder}
                className={`${baseFieldClasses} ${readOnlyClasses} h-9`}
            />
        </div>
    );
}

interface SectionProps {
    title: string;
    children: React.ReactNode;
    className?: string;
    enabled?: boolean;
    onToggle?: () => void;
}

function Section({ title, children, className = '', enabled, onToggle }: SectionProps) {
    const isToggleable = onToggle !== undefined && enabled !== undefined;
    const isDisabled = isToggleable && !enabled;
    return (
        <section className={`flex flex-col ${className}`}>
            <div className="flex items-center justify-between mb-3">
                <h3
                    className={`text-xs font-semibold uppercase tracking-wider ${isDisabled ? 'text-gray-300' : 'text-gray-400'}`}
                >
                    {title}
                </h3>
                {isToggleable && <SectionToggle enabled={enabled} onToggle={onToggle} />}
            </div>
            <div className={isDisabled ? 'opacity-40 pointer-events-none select-none' : undefined}>
                {children}
            </div>
        </section>
    );
}

function ClinicalFormStep({
    patientId,
    fields,
    onFieldChange,
    nextAppointment,
    onNextAppointmentChange,
    nextAppointmentLabel,
    nextAppointmentPlaceholder,
    alignerWearOptions,
    orthopulseStatus,
    toggles,
    onToggleChange
}: ClinicalFormStepProps) {
    const { data: card } = usePatientCard(patientId);

    const alignerFields = toggles.alignerFields ?? DEFAULT_ALIGNER_FIELDS;

    const handleAlignerFieldsChange = (next: AlignerFieldToggles) => {
        onToggleChange({
            ...toggles,
            alignerFields: next,
            alignerScheduleEnabled: anyAlignerFieldEnabled(next)
        });
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-2xl mx-auto">
            <Section title="Patient Info">
                <div className="grid grid-cols-2 gap-3">
                    <Field
                        label="Date"
                        value={fields.date}
                        onChange={(v) => onFieldChange('date', v)}
                        type="date"
                    />
                    <Field
                        label="Patient Name"
                        value={fields.patientName}
                        onChange={() => {}}
                        readOnly
                    />
                    <Field
                        label="Case Type"
                        value={card?.treatment?.caseType ?? '—'}
                        onChange={() => {}}
                        readOnly
                    />
                    <Field
                        label="Sub Case Type"
                        value={card?.treatment?.subCaseType ?? '—'}
                        onChange={() => {}}
                        readOnly
                    />
                </div>
            </Section>

            <Section title="Aligner Schedule">
                <AlignerScheduleSection
                    numberOfAligners={fields.numberOfAligners}
                    onNumberOfAlignersChange={(v) => onFieldChange('numberOfAligners', v)}
                    nextAppointment={nextAppointment}
                    onNextAppointmentChange={onNextAppointmentChange}
                    nextAppointmentLabel={nextAppointmentLabel}
                    nextAppointmentPlaceholder={nextAppointmentPlaceholder}
                    dmScanFrequency={fields.dmScanFrequency}
                    onDmScanFrequencyChange={(v) => onFieldChange('dmScanFrequency', v)}
                    alignerChangeFrequency={fields.alignerChangeFrequency}
                    onAlignerChangeFrequencyChange={(v) =>
                        onFieldChange('alignerChangeFrequency', v)
                    }
                    alignerWear={fields.alignerWear}
                    onAlignerWearChange={(v) => onFieldChange('alignerWear', v)}
                    alignerWearOptions={alignerWearOptions}
                    orthopulseStatus={orthopulseStatus}
                    toggles={alignerFields}
                    onTogglesChange={handleAlignerFieldsChange}
                />
            </Section>
        </div>
    );
}

export { ClinicalFormStep };
